import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI, proveedoresAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Bird, CalendarBlank, CurrencyDollar, Hash, ArrowsLeftRight, Warehouse, FolderOpen, Users } from '@phosphor-icons/react';

// Componente Input con formato de miles
const FormattedNumberInput = ({ value, onChange, placeholder, className, id, name, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value?.toString() || '');

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value?.toString() || '');
    }
  }, [value, isFocused]);

  const formatWithCommas = (num) => {
    if (!num && num !== 0) return '';
    return Number(num).toLocaleString('en-US');
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const numValue = parseInt(localValue.replace(/,/g, '')) || 0;
    onChange({ target: { name, value: numValue.toString() } });
  };

  const handleChange = (e) => {
    // Allow only numbers
    const val = e.target.value.replace(/[^0-9]/g, '');
    setLocalValue(val);
  };

  return (
    <Input
      id={id}
      name={name}
      type="text"
      className={className}
      value={isFocused ? localValue : formatWithCommas(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      {...props}
    />
  );
};

export default function LoteNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(true);
  const [formData, setFormData] = useState({
    nombre: '',
    nave: '',
    proyecto: '',
    cantidad_pollitos: '',
    precio_pollito: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    tasa_cambio: '61.5',
    moneda_pollitos: 'USD',
    proveedor_pollitos_id: '',
  });

  useEffect(() => {
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      setProveedores(response.data || []);
    } catch (error) {
      console.error('Error loading proveedores:', error);
    } finally {
      setLoadingProveedores(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.cantidad_pollitos || !formData.precio_pollito) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }
    
    if (!formData.proveedor_pollitos_id) {
      toast.error('Debe seleccionar un proveedor de pollitos');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nombre: formData.nombre,
        nave: formData.nave || null,
        proyecto: formData.proyecto || null,
        cantidad_pollitos: parseInt(formData.cantidad_pollitos),
        precio_pollito: parseFloat(formData.precio_pollito),
        fecha_inicio: formData.fecha_inicio || null,  // Enviar como YYYY-MM-DD sin conversión a UTC
        tasa_cambio: parseFloat(formData.tasa_cambio) || 61.5,
        moneda_pollitos: formData.moneda_pollitos,
        proveedor_pollitos_id: formData.proveedor_pollitos_id || null,
      };
      
      const response = await lotesAPI.create(payload);
      toast.success('Lote creado exitosamente');
      navigate(`/lotes/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear el lote';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const tasa = parseFloat(formData.tasa_cambio) || 61.5;
  const precioNum = parseFloat(formData.precio_pollito) || 0;
  const cantidadNum = parseInt(formData.cantidad_pollitos) || 0;
  
  // Calcular costo en ambas monedas
  const costoOriginal = cantidadNum * precioNum;
  const costoDOP = formData.moneda_pollitos === 'USD' ? costoOriginal * tasa : costoOriginal;
  const costoUSD = formData.moneda_pollitos === 'USD' ? costoOriginal : costoOriginal / tasa;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto animate-fade-in" data-testid="new-lote-form">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Nuevo Lote
          </h1>
          <p className="text-muted-foreground">
            Registre un nuevo lote de pollos de engorde
          </p>
        </div>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bird size={24} className="text-[#E73425]" weight="fill" />
              Información del Lote
            </CardTitle>
            <CardDescription>
              Complete los datos iniciales para comenzar el seguimiento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre del Lote *</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  placeholder="Ej: Lote 1, Enero 2024"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="input-focus"
                  data-testid="lote-nombre"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nave" className="flex items-center gap-2">
                    <Warehouse size={16} />
                    Nave #
                  </Label>
                  <Input
                    id="nave"
                    name="nave"
                    placeholder="Ej: 1, A, Norte"
                    value={formData.nave}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="lote-nave"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="proyecto" className="flex items-center gap-2">
                    <FolderOpen size={16} />
                    Proyecto
                  </Label>
                  <Input
                    id="proyecto"
                    name="proyecto"
                    placeholder="Ej: Proyecto Principal"
                    value={formData.proyecto}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="lote-proyecto"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cantidad_pollitos" className="flex items-center gap-2">
                    <Hash size={16} />
                    Cantidad de Pollitos *
                  </Label>
                  <FormattedNumberInput
                    id="cantidad_pollitos"
                    name="cantidad_pollitos"
                    placeholder="Ej: 20,000"
                    value={formData.cantidad_pollitos}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="lote-cantidad"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="precio_pollito" className="flex items-center gap-2">
                    <CurrencyDollar size={16} />
                    Precio por Pollito ($) *
                  </Label>
                  <Input
                    id="precio_pollito"
                    name="precio_pollito"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Ej: 0.50"
                    value={formData.precio_pollito}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="lote-precio"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="proveedor_pollitos_id" className="flex items-center gap-2">
                  <Users size={16} />
                  Proveedor de Pollitos *
                </Label>
                <Select
                  value={formData.proveedor_pollitos_id || "none"}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, proveedor_pollitos_id: value === "none" ? "" : value }))}
                >
                  <SelectTrigger data-testid="lote-proveedor">
                    <SelectValue placeholder={loadingProveedores ? "Cargando..." : "Seleccione un proveedor"} />
                  </SelectTrigger>
                  <SelectContent>
                    {proveedores.map((prov) => (
                      <SelectItem key={prov.id} value={prov.id}>
                        {prov.nombre} {prov.rnc ? `(${prov.rnc})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Al crear el lote, se registrará automáticamente el gasto de pollitos a crédito con este proveedor.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_inicio" className="flex items-center gap-2">
                  <CalendarBlank size={16} />
                  Fecha de Inicio
                </Label>
                <Input
                  id="fecha_inicio"
                  name="fecha_inicio"
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={handleChange}
                  className="input-focus"
                  data-testid="lote-fecha"
                />
              </div>

              {/* Configuración de Moneda */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <ArrowsLeftRight size={18} className="text-[#F69031]" />
                  Configuración de Moneda
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tasa_cambio">Tasa de Cambio (USD → DOP)</Label>
                    <Input
                      id="tasa_cambio"
                      name="tasa_cambio"
                      type="number"
                      step="0.01"
                      min="1"
                      placeholder="61.50"
                      value={formData.tasa_cambio}
                      onChange={handleChange}
                      className="input-focus"
                      data-testid="lote-tasa"
                    />
                    <p className="text-xs text-muted-foreground">1 USD = {formData.tasa_cambio} DOP</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="moneda_pollitos">Moneda del Precio de Pollitos</Label>
                    <Select
                      value={formData.moneda_pollitos}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, moneda_pollitos: value }))}
                    >
                      <SelectTrigger data-testid="lote-moneda">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD (Dólar)</SelectItem>
                        <SelectItem value="DOP">DOP (Peso Dominicano)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Cost Preview */}
              <div className="bg-muted/50 rounded-lg p-4 border border-border/60">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Costo Inicial de Pollitos</p>
                    <p className="text-xs text-muted-foreground">
                      {cantidadNum.toLocaleString()} pollitos × {formData.moneda_pollitos === 'USD' ? '$' : 'RD$'}{precioNum.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#E73425]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      RD$ {costoDOP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ≈ US$ {costoUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/lotes')}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#E73425] hover:bg-[#D81C23] btn-animate"
                  disabled={loading}
                  data-testid="submit-lote"
                >
                  {loading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    <>
                      <Bird size={18} className="mr-2" />
                      Crear Lote
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-border/60 bg-blue-50/50">
          <CardContent className="pt-6">
            <h4 className="font-semibold mb-2">📋 Próximos pasos después de crear el lote:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Fase 0:</strong> Registrar gastos de acondicionamiento (limpieza, virutas)</li>
              <li>• <strong>Fase 1:</strong> Registrar factura de pollitos y alimento inicial</li>
              <li>• <strong>Fase 1.5:</strong> Registrar facturas de alimento y medicamentos</li>
              <li>• <strong>Fase 2:</strong> Registrar mano de obra, vacunas, gas y alquiler</li>
              <li>• <strong>Fase 3:</strong> Registrar pagos de préstamos (cada 60 días)</li>
              <li>• <strong>Día 45:</strong> Cerrar lote con el ingreso de venta</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
